import i18n from '@/i18n'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import type { User } from '@/types/auth.types'
import { UserRole } from '@/types/enums'
import { authService } from '@/services/auth.service'
import { setAuthTokens } from '@/services/api'
import { API_CONFIG } from '@/config/api.config'
import router from '@/router'

export type SessionCheckResult = { ok: true } | { ok: false; loginPath: string }

function parseJwtExp(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

const TOKEN_KEY = 'ik_access_token'
const REFRESH_KEY = 'ik_refresh_token'
const USER_KEY = 'ik_user'

export const useAuthStore = defineStore('auth', () => {
  // ─── State ──────────────────────────────────────────────────
  const user = ref<User | null>(null)
  const accessToken = ref<string>('')
  const refreshToken = ref<string>('')
  const isLoading = ref(false)
  const error = ref<string>('')
  const isRestoringSession = ref(false)

  // ─── Getters ────────────────────────────────────────────────
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)

  const fullName = computed(() => {
    if (!user.value) return ''
    return `${user.value.firstName} ${user.value.lastName}`
  })

  const role = computed(() => user.value?.role || null)
  const isAdmin = computed(
    () =>
      role.value === UserRole.ADMIN ||
      role.value === UserRole.SUPER_ADMIN ||
      role.value === UserRole.EXECUTIVE ||
      role.value === UserRole.COMPANY_DIRECTOR
  )
  const isHR = computed(() => role.value === UserRole.HR || role.value === UserRole.HR_MANAGER)
  const isFinance = computed(() => role.value === UserRole.FINANCE)
  const isOperations = computed(() => role.value === UserRole.OPERATIONS)
  const isManager = computed(() => role.value === UserRole.MANAGER)
  const isEmployee = computed(() => role.value === UserRole.EMPLOYEE)
  const companyId = computed(() => user.value?.companyId || '')
  const isPortalUser = computed(() => user.value?.isPortalUser || false)
  const mustChangePassword = computed(() => user.value?.mustChangePassword || false)

  // ─── Private helpers ────────────────────────────────────────
  function persistAuth() {
    localStorage.setItem(TOKEN_KEY, accessToken.value)
    localStorage.setItem(REFRESH_KEY, refreshToken.value)
    if (user.value) {
      localStorage.setItem(USER_KEY, JSON.stringify(user.value))
    }
  }

  function clearAuth() {
    accessToken.value = ''
    refreshToken.value = ''
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
    authService.clearAuthTokens()
  }

  function setAuthData(userData: User, tokens: { accessToken: string; refreshToken: string }) {
    user.value = userData
    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
    authService.setAuthTokens(tokens.accessToken, tokens.refreshToken)
    persistAuth()
  }

  async function restoreSessionFromCookie() {
    if (isAuthenticated.value || isRestoringSession.value) {
      return isAuthenticated.value
    }

    isRestoringSession.value = true
    try {
      const refreshResponse = await axios.post(
        `${API_CONFIG.baseURL}${API_CONFIG.refreshEndpoint}`,
        {},
        { withCredentials: true }
      )
      const newAccess =
        refreshResponse.data?.data?.accessToken ?? refreshResponse.data?.accessToken
      const newRefresh =
        refreshResponse.data?.data?.refreshToken ?? refreshResponse.data?.refreshToken

      if (!newAccess) {
        clearAuth()
        return false
      }

      const meResponse = await authService.me()
      const currentUser = meResponse?.data
      if (!currentUser) {
        clearAuth()
        return false
      }

      setAuthData(currentUser, {
        accessToken: newAccess,
        refreshToken: newRefresh || refreshToken.value
      })
      return true
    } catch {
      clearAuth()
      return false
    } finally {
      isRestoringSession.value = false
    }
  }

  // ─── Actions ────────────────────────────────────────────────
  async function login(email: string, password: string) {
    isLoading.value = true
    error.value = ''
    try {
      const response = await authService.login({ email, password })
      if (response.success && response.data) {
        setAuthData(response.data.user, response.data.tokens)
        return true
      } else {
        error.value = response.message || i18n.global.t('workflow.loginFailedStore')
        return false
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || i18n.global.t('workflow.loginError')
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function portalLogin(
    username: string,
    password: string,
    companyDomain?: string,
    consents?: { kvkkAccepted?: boolean; usageAccepted?: boolean },
  ) {
    isLoading.value = true
    error.value = ''
    try {
      const response = await authService.portalLogin({
        username,
        password,
        ...(companyDomain?.trim() ? { companyDomain: companyDomain.trim() } : {}),
        ...(consents?.kvkkAccepted ? { kvkkAccepted: true } : {}),
        ...(consents?.usageAccepted ? { usageAccepted: true } : {}),
      })
      if (response.success && response.data) {
        setAuthData(response.data.user, response.data.tokens)
        return true
      } else {
        error.value = response.message || i18n.global.t('workflow.loginFailedStore')
        return false
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || i18n.global.t('workflow.loginError')
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function changePassword(newPassword: string, confirmPassword: string) {
    isLoading.value = true
    error.value = ''
    try {
      await authService.changePassword(newPassword, confirmPassword)
      if (user.value) {
        user.value = { ...user.value, mustChangePassword: false }
        persistAuth()
      }
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || i18n.global.t('workflow.passwordChangeError')
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    const wasPortal = user.value?.isPortalUser
    try {
      await authService.logout()
    } catch {
      // Ignore logout API errors
    } finally {
      clearAuth()
      router.push(
        wasPortal ? { path: '/login', query: { tab: 'employee' } } : { path: '/login' },
      )
    }
  }

  /**
   * Restore auth state from localStorage.
   * Called once on app startup before the first navigation guard runs.
   */
  function initAuth() {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedRefreshToken = localStorage.getItem(REFRESH_KEY)
    const storedUser = localStorage.getItem(USER_KEY)

    if (storedToken && storedRefreshToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser) as User
        accessToken.value = storedToken
        refreshToken.value = storedRefreshToken
        user.value = parsedUser
        authService.setAuthTokens(storedToken, storedRefreshToken)
      } catch {
        // Corrupted data → clear everything
        clearAuth()
      }
    } else {
      // Partial data → clean up
      clearAuth()
    }
  }

  /**
   * If access JWT is expired, refresh once (same contract as api interceptor).
   * Used from router so users are not left on protected routes with a dead access token.
   */
  async function ensureValidAccessToken(): Promise<SessionCheckResult> {
    const token = accessToken.value
    if (!token) {
      return { ok: false, loginPath: '/login' }
    }

    const exp = parseJwtExp(token)
    if (exp !== null) {
      const now = Math.floor(Date.now() / 1000)
      const skewSec = 30
      if (exp <= now + skewSec) {
        const rt = refreshToken.value
        if (!rt) {
          const loginPath = '/login'
          clearAuth()
          return { ok: false, loginPath }
        }

        try {
          const response = await axios.post(
            `${API_CONFIG.baseURL}${API_CONFIG.refreshEndpoint}`,
            { refreshToken: rt },
            { withCredentials: true }
          )
          const newAccess =
            response.data?.data?.accessToken ?? response.data?.accessToken
          const newRefresh =
            response.data?.data?.refreshToken ?? response.data?.refreshToken
          if (!newAccess) {
            const loginPath = '/login'
            clearAuth()
            return { ok: false, loginPath }
          }
          accessToken.value = newAccess
          if (newRefresh) {
            refreshToken.value = newRefresh
          }
          setAuthTokens(newAccess, newRefresh || rt)
          persistAuth()
        } catch (refreshError) {
          // Ağ hatası / 5xx geçicidir; oturumu yalnız sunucu token'ı reddettiyse bitir
          const status = (refreshError as { response?: { status?: number } })?.response?.status
          if (status !== 401 && status !== 403) {
            return { ok: true }
          }
          const loginPath = '/login'
          clearAuth()
          return { ok: false, loginPath }
        }
      }
    }

    return { ok: true }
  }

  return {
    // state
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    // getters
    isAuthenticated,
    fullName,
    role,
    isAdmin,
    isHR,
    isFinance,
    isOperations,
    isManager,
    isEmployee,
    companyId,
    isPortalUser,
    mustChangePassword,
    // actions
    login,
    portalLogin,
    changePassword,
    logout,
    initAuth,
    clearAuth,
    ensureValidAccessToken,
    restoreSessionFromCookie
  }
})
